import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { dashboard } from '../../services/api';
import { DateRangePicker, Loading, SectionBlock, StatCard } from '../ui';

const STAT_LABELS = {
  director: [
    { key: 'newManagers', label: 'New Managers' },
    { key: 'newPromoters', label: 'New Promoters' },
    { key: 'newRegisteredUsers', label: 'New Registered Users' },
    { key: 'newLoanSuccessUsers', label: 'New Loan Success Users' }
  ],
  manager: [
    { key: 'newPromoters', label: 'New Promoters' },
    { key: 'newRegisteredUsers', label: 'New Registered Users' },
    { key: 'newLoanSuccessUsers', label: 'New Loan Success Users' }
  ],
  promoter: [
    { key: 'newRegisteredUsers', label: 'New Registered Users' },
    { key: 'newLoanSuccessUsers', label: 'New Loan Success Users' }
  ],
  admin: [
    { key: 'newDirectors', label: 'New Directors' },
    { key: 'newManagers', label: 'New Managers' },
    { key: 'newPromoters', label: 'New Promoters' },
    { key: 'newRegisteredUsers', label: 'New Registered Users' },
    { key: 'newLoanSuccessUsers', label: 'New Loan Success Users' }
  ]
};

export default function ProgressStatsSection() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dateRange, setDateRange] = useState({ fromDate: '', toDate: '' });
  const [error, setError] = useState(null);
  
  const tier = user?.tier;
  
  const loadStats = useCallback(async (fromDate, toDate) => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      
      const res = await dashboard.progressStats(params);
      setStats(res.data.stats);
      setDateRange({
        fromDate: res.data.dateRange.fromDate,
        toDate: res.data.dateRange.toDate,
        days: res.data.dateRange.days
      });
    } catch (err) {
      console.error('Failed to load progress stats:', err);
      setError(err.response?.data?.error || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);
  
  useEffect(() => {
    loadStats();
  }, [loadStats]);
  
  const handleDateChange = useCallback((fromDate, toDate) => {
    loadStats(fromDate, toDate);
  }, [loadStats]);
  
  if (tier === undefined || tier === null) {
    return null;
  }
  
  const roleKey = tier === 0 ? 'admin' : tier === 1 ? 'director' : tier === 2 ? 'manager' : tier === 3 ? 'promoter' : 'admin';
  const statLabels = STAT_LABELS[roleKey] || [];
  
  return (
    <SectionBlock
      title={`${dateRange.days || 7} Days Progress Statistics`}
      actions={
        <DateRangePicker
          fromDate={dateRange.fromDate}
          toDate={dateRange.toDate}
          onChange={handleDateChange}
        />
      }
    >
      {loading ? (
        <Loading text="Loading statistics..." />
      ) : error ? (
        <div className="rounded-[16px] border border-red-300/18 bg-red-300/10 p-4 text-sm text-red-100">
          {error}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {statLabels.map(({ key, label }) => (
            <StatCard
              key={key}
              label={label}
              value={stats?.[key] ?? 0}
            />
          ))}
        </div>
      )}
    </SectionBlock>
  );
}
